import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";
import { Section } from "@/components/common/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { site, telLink, mailLink, waLink } from "@/config/site";
import { services } from "@/data/content";

export function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const buildMessage = () =>
    `New Enquiry — BNZ Solutions\n\nName: ${form.name || "-"}\nPhone: ${form.phone || "-"}\nService: ${form.service || "-"}\nMessage: ${form.message || "-"}`;

  const submitWhatsApp = (e) => {
    e.preventDefault();
    window.open(
      `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(buildMessage())}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Section id="contact" bg="navy">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        {/* Left: info + quick actions */}
        <div>
          <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-gold-light">Get in Touch</span>
          <div className="gold-rule my-4" />
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight text-white">
            Let’s brand your workspace
          </h2>
          <p className="mt-4 text-white/65 leading-relaxed max-w-lg">
            Tell us about your project and we’ll get back with a transparent quote. Prefer to talk? Call or WhatsApp us directly.
          </p>

          <div className="mt-8 space-y-3">
            <a href={telLink} data-testid="contact-call" className="flex items-center gap-4 border border-white/15 p-4 transition-colors hover:border-gold group">
              <span className="flex h-11 w-11 items-center justify-center bg-white/5 text-gold group-hover:bg-gold group-hover:text-navy transition-colors"><Phone className="h-5 w-5" /></span>
              <span><span className="block text-xs uppercase tracking-widest text-white/50">Call Us</span><span className="text-white font-medium">{site.phone}</span></span>
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 border border-white/15 p-4 transition-colors hover:border-gold group">
              <span className="flex h-11 w-11 items-center justify-center bg-white/5 text-gold group-hover:bg-gold group-hover:text-navy transition-colors"><MessageCircle className="h-5 w-5" /></span>
              <span><span className="block text-xs uppercase tracking-widest text-white/50">WhatsApp</span><span className="text-white font-medium">Chat with us</span></span>
            </a>
            <a href={mailLink()} data-testid="contact-email" className="flex items-center gap-4 border border-white/15 p-4 transition-colors hover:border-gold group">
              <span className="flex h-11 w-11 items-center justify-center bg-white/5 text-gold group-hover:bg-gold group-hover:text-navy transition-colors"><Mail className="h-5 w-5" /></span>
              <span><span className="block text-xs uppercase tracking-widest text-white/50">Email</span><span className="text-white font-medium">{site.email}</span></span>
            </a>
            <div className="flex items-center gap-4 border border-white/15 p-4">
              <span className="flex h-11 w-11 items-center justify-center bg-white/5 text-gold"><MapPin className="h-5 w-5" /></span>
              <span><span className="block text-xs uppercase tracking-widest text-white/50">Location</span><span className="text-white font-medium">{site.address.city}, {site.address.state}</span></span>
            </div>
          </div>

          {/* Google Maps embed placeholder */}
          <div className="mt-6 overflow-hidden border border-white/15">
            <iframe
              title="BNZ Solutions location map"
              src={site.mapEmbedUrl}
              width="100%"
              height="220"
              style={{ border: 0, filter: "grayscale(0.2)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>

        {/* Right: enquiry form */}
        <div className="bg-white p-7 md:p-9">
          <h3 className="font-heading text-2xl text-navy">Request a Quote</h3>
          <p className="mt-1 text-sm text-charcoal/60">Submitting opens WhatsApp with your details prefilled.</p>
          <form className="mt-6 space-y-5" onSubmit={submitWhatsApp} data-testid="quote-form">
            <div>
              <Label htmlFor="name" className="text-charcoal">Full Name</Label>
              <Input id="name" data-testid="form-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="mt-2 rounded-none border-black/15 focus-visible:ring-gold" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-charcoal">Phone Number</Label>
              <Input id="phone" data-testid="form-phone" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="mt-2 rounded-none border-black/15 focus-visible:ring-gold" />
            </div>
            <div>
              <Label className="text-charcoal">Service Required</Label>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger data-testid="form-service" className="mt-2 rounded-none border-black/15 focus:ring-gold">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {services.map((s) => (
                    <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="Other / Multiple">Other / Multiple</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="message" className="text-charcoal">Project Details</Label>
              <Textarea id="message" data-testid="form-message" value={form.message} onChange={set("message")} placeholder="Tell us about your requirement..." rows={4} className="mt-2 rounded-none border-black/15 focus-visible:ring-gold" />
            </div>
            <button
              type="submit"
              data-testid="form-submit"
              className="inline-flex w-full items-center justify-center gap-2 bg-gold px-6 py-4 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-gold-dark hover:text-white"
            >
              <Send className="h-4 w-4" /> Send Enquiry via WhatsApp
            </button>
          </form>
        </div>
      </div>
    </Section>
  );
}
